import { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useCreateSolarEstimationMutation,
  useInitiateSolarEstimationMutation,
  useLazyViewSolarEstimationQuery,
  useUpdateEstimationMutation,
} from "../../../redux/api/solar-estimation/solarEstimationAPI";
import { selectAuth } from "../../../redux/features/auth/auth-slice";
import { selectCountry } from "../../../redux/features/user/user-slice";
import {
  IViewSolarEstimationData,
  IViewSolarEstimationOutputParams,
  IViewSolarUserSelectedProducts,
  ViewSolarEstimationResponseDTO,
} from "../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-response-dto";
import { EApplicationType } from "../../../shared/oversight-core/enums/application-type";
import { EEnergyNeedEditParameter } from "../../../shared/oversight-core/enums/energy-need-edit-parameters";
import { EEstimationType } from "../../../shared/oversight-core/enums/estimation-type";
import { EInverterPhaseType } from "../../../shared/oversight-core/enums/inverter-phase-type";
import { ESolarSolutionType } from "../../../shared/oversight-core/enums/sola-solution-type";
import { ESolarSimBrands } from "../../../shared/oversight-core/enums/solar-sim-brands";
import { ESolarSimLocalStorage } from "../../../shared/oversight-core/enums/solar-sim-local-storage";
import { ESolarSystemType } from "../../../shared/oversight-core/enums/solar-system-type ";
import { EUtilityOptionType } from "../../../shared/oversight-core/enums/utility-option-type";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import { Configurations } from "../../../shared/oversight-core/interfaces/configurations";
import { IDirectSave } from "../../../shared/oversight-core/interfaces/direct-save";
import { IEstimatorDetails } from "../../../shared/oversight-core/interfaces/estimator-details";
import { ISolarEstimationIdAndToken } from "../../../shared/oversight-core/interfaces/solar-estimation-id-and-token";
import { Option } from "../../../shared/oversight-core/ui-elements/app-select/app-select";
import MaterialIcon from "../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import { ISteps } from "../../../shared/oversight-core/ui-elements/steps-number/steps-number";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/oversight-core/utils/toast";
import ApprovedSystem from "../components/approved-system/approved-system";
import AssembleSystem from "../components/assemble-system/assemble-system";
import EditParameterModal from "../components/edit-parameter-modal/edit-parameter-modal";
import IncludeSystemCard from "../components/include-system-card/include-system-card";
import RemoveSolarSystemModal from "../components/remove-solar-system-modal/remove-solar-system-modal";
import RemoveWaterHeaterSystemModal from "../components/remove-water-heater-system-modal/remove-water-heater-system-modal";
import SaveDataModal from "../components/save-data-modal/save-data-modal";
import SelectedSolarSystemCard from "../components/selected-solar-system-card/selected-solar-system-card";
import SelectedWaterHeaterSystemCard from "../components/selected-water-heater-system-card/selected-water-heater-system-card";
import StepsHeader from "../components/steps-header/steps-header";
import WaterHeaterSystemModal from "../components/water-heater-system-modal/water-heater-system-modal";
import styles from "./build-system-direct.module.scss";

const steps: ISteps[] = [
  { id: 1, stepName: "Assemble System", stepIcon: "widgets" },
];

const solarSystemTypesOption: Option[] = [
  {
    value: ESolarSystemType.BACKUP,
    label: "Backup",
  },
  {
    value: ESolarSystemType.OFF_GRID,
    label: "Off-Grid",
  },
  {
    value: ESolarSystemType.HYBRID,
    label: "Hybrid",
  },
];

const inverterPhaseTypeOptions: Option[] = [
  {
    value: EInverterPhaseType.SINGLE_PHASE,
    label: "1 Phase",
  },
  {
    value: EInverterPhaseType.THREE_PHASE,
    label: "3 Phase",
  },
  {
    value: EInverterPhaseType.NONE,
    label: "None",
  },
];

const applicationTypeOptions: Option[] = [
  {
    value: EApplicationType.RESIDENTIAL,
    label: "Residential",
  },
  {
    value: EApplicationType.HOTELS,
    label: "Hotels",
  },
  {
    value: EApplicationType.HOSTELS,
    label: "Hostels",
  },
  {
    value: EApplicationType.SCHOOLS,
    label: "Schools",
  },
];

const utilityOptions: Option[] = [
  {
    value: EUtilityOptionType.SHOWER,
    label: "Shower",
  },
  {
    value: EUtilityOptionType.BATHTUB_JACUZZI,
    label: "Bathtub/Jacuzzi",
  },
];

const systemVoltageOptions: Option[] = [
  {
    value: "12",
    label: "12V",
  },
  {
    value: "24",
    label: "24V",
  },
  {
    value: "48",
    label: "48V",
  },
];

interface ILocationState {
  systemType: ESolarSystemType;
  monthlyEnergyNeed: number;
  maxPowerNeed: number;
  nightLoad: number;
  systemVoltage: number;
  autonomyDays: number;
  sunlightHours: number;
  numberOfUsers: number;
  demandPerUser: number;
  applicationType: EApplicationType;
  utilityOption: EUtilityOptionType;
  solarSolutionType: ESolarSolutionType;
  inverterPhaseType: EInverterPhaseType;
  estimationId: string;
  estimationToken: string;
}

const BuildSystemDirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useSelector(selectAuth);
  const country = useSelector(selectCountry);
  const pathname = location.pathname;
  const locationState = location.state as ILocationState;

  const [selectedStep, setSelectedStep] = useState(1);
  const [estimatorDetails, setEstimatorDetails] = useState<IEstimatorDetails>({
    estimationId: "",
    estimationToken: "",
  });
  const [configurations, setConfigurations] = useState<Configurations>();
  const [title, setTitle] = useState("");
  const [monthlyEnergyNeed, setMonthlyEnergyNeed] = useState(0);
  const [maxPower, setMaxPower] = useState(0);
  const [autonomyDays, setAutonomyDays] = useState<number | null>(null);
  const [dischargeRate, setDischargeRate] = useState(0);
  const [nightLoad, setNightLoad] = useState(0);
  const [waterDemand, setWaterDemand] = useState(0);
  const [sunlightHours, setSunlightHours] = useState(5);
  const [assembleSystemData, setAssembleSystemData] = useState<
    IViewSolarEstimationData | undefined
  >(undefined);
  const [isResetRecommendations, setIsResetRecommendations] = useState(false);
  const [selectedSystemOutput, setSelectedSystemOutput] =
    useState<IViewSolarEstimationOutputParams>({
      totalWattHours: 0,
      totalCost: 0,
      systemVoltage: 0,
      totalAmpHours: 0,
      nightLoadAmpHours: 0,
      batteryAmpHours: 0,
      maxPowerInWatt: 0,
      grandTotal: 0,
      totalAccessoriesCost: 0,
      laborCost: 0,
      totalLiters: 0,
    });
  const [isShowSaveDataModal, setIsShowSaveDataModal] = useState(false);
  const [showEditParameterModal, setShowEditParameterModal] = useState(false);
  const [editParameterType, setEditParameterType] =
    useState<EEnergyNeedEditParameter>(EEnergyNeedEditParameter.TITLE);
  const [editParameterValue, setEditParameterValue] = useState<number | string>(
    0,
  );
  const [estimatedValue, setEstimatedValue] = useState<number | undefined>(
    undefined,
  );
  const [userSelectedProducts, setUserSelectedProducts] = useState<
    IViewSolarUserSelectedProducts[]
  >([]);
  const [currency, setCurrency] = useState("");
  const [includeOtherFees, setIncludeOtherFees] = useState(false);
  const [estimationId, setEstimationId] = useState(0);
  const [comment, setComment] = useState("");
  const [isReset, setIsReset] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [solarSystemType, setSolarSystemType] = useState<Option>({
    ...solarSystemTypesOption[2],
  });
  const [systemVoltage, setSystemVoltage] = useState<Option | null>(null);
  const [inverterPhaseType, setInverterPhaseType] = useState<Option>({
    ...inverterPhaseTypeOptions[0],
  });
  const [applicationType, setApplicationType] = useState<Option>({
    ...applicationTypeOptions[0],
  });
  const [solarSolutionType, setSolarSolutionType] =
    useState<ESolarSolutionType>(ESolarSolutionType.BOTH);
  const [utilityOption, setUtilityOption] = useState<Option>({
    ...utilityOptions[0],
  });
  const [numberOfUsers, setNumberOfUsers] = useState(0);
  const [demandPerUser, setDemandPerUser] = useState<number | null>(null);
  const [isUpdateCurrentState, setIsUpdateCurrentState] = useState(false);
  const [isShowRemoveSolarSystemModal, setIsShowRemoveSolarSystemModal] =
    useState(false);
  const [
    isShowRemoveWaterHeaterSystemModal,
    setIsShowRemoveWaterHeaterSystemModal,
  ] = useState(false);
  const [isRemoveWaterHeaterSystem, setIsRemoveWaterHeaterSystem] =
    useState(false);
  const [isShowWaterHeaterSystemModal, setIsShowWaterHeaterSystemModal] =
    useState(false);
  const [isAddWaterHeaterSystem, setIsAddWaterHeaterSystem] = useState(false);
  const [controllerRequired, setControllerRequired] = useState(false);

  const hasHydrated = useRef(false);

  const [
    triggerInitiateSolarEstimation,
    { isLoading: isLoadingInitiateSolarEstimation },
  ] = useInitiateSolarEstimationMutation();
  const [
    triggerCreateSolarEstimation,
    { isLoading: isLoadingCreateSolarEstimation },
  ] = useCreateSolarEstimationMutation();
  const [
    triggerLazyViewSolarEstimation,
    { isLoading: isLoadingViewEstimation },
  ] = useLazyViewSolarEstimationQuery();
  const [triggerUpdateEstimation, { isLoading: isLoadingUpdateEstimation }] =
    useUpdateEstimationMutation();

  useEffect(() => {
    const globalConfigurations = localStorage.getItem("globalConfigurations");

    let mounted = true;

    const applyResponse = (res: ViewSolarEstimationResponseDTO) => {
      if (!mounted) return;

      const view = res?.estimationView;
      const input = view?.data?.inputParams ?? {};

      const selectedSolarTypeOption = solarSystemTypesOption.find(
        (s) => s.value === input.systemType,
      );

      const selectedSystemVoltageOption = systemVoltageOptions.find(
        (s) => Number(s.value) === input.systemVoltage,
      );

      const selectedInverterPhaseTypeOption = inverterPhaseTypeOptions.find(
        (i) => i.value === view?.inverterPhaseType,
      );

      const selectedUtilityOption = utilityOptions.find(
        (u) => u.value === view?.utilityOptionType,
      );

      const selectedApplicationType = applicationTypeOptions.find(
        (a) => a.value === view?.applicationType,
      );

      setNumberOfUsers(view?.data.inputParams.numberOfUsers || 0);
      setAutonomyDays(input.autonomyDays || 0);
      setDemandPerUser(view?.data.inputParams.demandPerUser || 0);
      setSolarSolutionType(view?.estimationType);
      setTitle((ps) => view?.title ?? ps);
      setDischargeRate(input.dischargeRatePercentage ?? 0);
      setNightLoad(input.nightLoadPercentage ?? 60);
      setMonthlyEnergyNeed(input.monthlyRequiredEnergy ?? 0);
      setMaxPower(input.maxRequiredPower ?? 0);
      setSunlightHours(input.sunlightHours ?? 5);
      setIncludeOtherFees(Boolean(view?.installationCostRequested));
      setEstimationId(view?.estimationId);
      setWaterDemand(input.waterDemand || 0);
      setControllerRequired(view?.controllerRequired);

      if (selectedSolarTypeOption) {
        setSolarSystemType({
          value: selectedSolarTypeOption.value,
          label: selectedSolarTypeOption.label,
        });
      }

      if (selectedSystemVoltageOption) {
        setSystemVoltage({
          value: selectedSystemVoltageOption.value,
          label: selectedSystemVoltageOption.label,
        });
      }

      if (selectedInverterPhaseTypeOption) {
        setInverterPhaseType({
          value: selectedInverterPhaseTypeOption.value,
          label: selectedInverterPhaseTypeOption.label,
        });
      }

      if (selectedApplicationType) {
        setApplicationType({
          value: selectedApplicationType.value,
          label: selectedApplicationType.label,
        });
      }

      if (selectedUtilityOption) {
        setUtilityOption({
          value: selectedUtilityOption.value,
          label: selectedUtilityOption.label,
        });
      }

      if (view?.data?.preferences) {
        if (globalConfigurations) {
          const parsedGlobalConfigurations: Configurations =
            JSON.parse(globalConfigurations);

          setConfigurations(parsedGlobalConfigurations);

          return;
        }

        setConfigurations(view.data.preferences);
      }
    };

    const shouldUseEstimationId = Boolean(
      auth.token && estimatorDetails.estimationId,
    );
    const shouldUseEstimatorToken = Boolean(
      !auth.token && estimatorDetails.estimationToken,
    );

    if (!shouldUseEstimationId && !shouldUseEstimatorToken) return;

    (async () => {
      try {
        const params = shouldUseEstimationId
          ? { estimationId: estimatorDetails.estimationId }
          : { estimatorToken: estimatorDetails.estimationToken };

        const res = await triggerLazyViewSolarEstimation(params).unwrap();
        applyResponse(res);
      } catch (err) {
        showErrorMessage("Failed to load estimation details");
      }
    })();

    return () => {
      mounted = false;
    };
  }, [
    auth.token,
    estimatorDetails.estimationId,
    estimatorDetails.estimationToken,
    isUpdateCurrentState,
  ]);

  useEffect(() => {
    if (solarSystemType.value) {
      setHasChanged(true);
    }
  }, [solarSystemType.value]);

  useEffect(() => {
    if (!estimatorDetails.estimationId || !estimatorDetails.estimationToken) {
      return;
    }

    if (hasHydrated.current) {
      return;
    }

    hasHydrated.current = true;

    const directSave = localStorage.getItem("directSave");

    if (directSave) {
      const parsedDirectSave: IDirectSave = JSON.parse(directSave);

      const selectedSolarTypeOption = solarSystemTypesOption.find(
        (s) => s.value === parsedDirectSave.systemType,
      );

      const selectedSystemVoltageOption = systemVoltageOptions.find(
        (s) => Number(s.value) === parsedDirectSave.systemVoltage,
      );

      const selectedInverterPhaseTypeOption = inverterPhaseTypeOptions.find(
        (i) => i.value === parsedDirectSave.inverterPhaseType,
      );

      const selectedUtilityOption = utilityOptions.find(
        (u) => u.value === parsedDirectSave.utilityOptionType,
      );

      const selectedApplicationType = applicationTypeOptions.find(
        (a) => a.value === parsedDirectSave.applicationType,
      );

      if (!parsedDirectSave.isCalledSave) {
        onSave(
          parsedDirectSave.title,
          parsedDirectSave.estimationType,
          parsedDirectSave.applicationType,
          parsedDirectSave.utilityOptionType,
          parsedDirectSave.systemType,
          parsedDirectSave.inverterPhaseType,
          parsedDirectSave.monthlyEnergyNeed,
          parsedDirectSave.maxPower,
          parsedDirectSave.numberOfUsers,
          parsedDirectSave.nightLoad,
          parsedDirectSave.sunlightHours,
          parsedDirectSave.demandPerUser,
          parsedDirectSave.systemVoltage,
          parsedDirectSave.autonomyDays,
        );
      }

      setTitle(parsedDirectSave.title);
      setMonthlyEnergyNeed(parsedDirectSave.monthlyEnergyNeed);
      setMaxPower(parsedDirectSave.maxPower);
      setNightLoad(parsedDirectSave.nightLoad);
      setAutonomyDays(parsedDirectSave.autonomyDays);
      setSunlightHours(parsedDirectSave.sunlightHours);
      setIncludeOtherFees(parsedDirectSave.includeOtherFees);
      setNumberOfUsers(parsedDirectSave.numberOfUsers);
      setDemandPerUser(parsedDirectSave.demandPerUser);
      setSolarSolutionType(parsedDirectSave.estimationType);

      if (selectedSolarTypeOption) {
        setSolarSystemType({
          value: selectedSolarTypeOption.value,
          label: selectedSolarTypeOption.label,
        });
      }

      if (selectedSystemVoltageOption) {
        setSystemVoltage({
          value: selectedSystemVoltageOption.value,
          label: selectedSystemVoltageOption.label,
        });
      }

      if (selectedInverterPhaseTypeOption) {
        setInverterPhaseType({
          value: selectedInverterPhaseTypeOption.value,
          label: selectedInverterPhaseTypeOption.label,
        });
      }

      if (selectedApplicationType) {
        setApplicationType({
          value: selectedApplicationType.value,
          label: selectedApplicationType.label,
        });
      }

      if (selectedUtilityOption) {
        setUtilityOption({
          value: selectedUtilityOption.value,
          label: selectedUtilityOption.label,
        });
      }
    }
  }, [estimatorDetails.estimationId, estimatorDetails.estimationToken]);

  useEffect(() => {
    if (pathname.includes("assemble-system")) {
      setSelectedStep(1);
      return;
    }

    if (pathname.includes("system-summary")) {
      setSelectedStep(2);
    }
  }, [pathname]);

  useEffect(() => {
    if (!pathname.includes("assemble-system")) return;

    const { estimationId, estimationToken } = estimatorDetails;

    if (!estimationId || !estimationToken) {
      const unparsedEstimatorDetails = localStorage.getItem(
        ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
      );

      if (unparsedEstimatorDetails) {
        const parsedEstimatorDetails: IEstimatorDetails = JSON.parse(
          unparsedEstimatorDetails,
        );
        setEstimatorDetails(parsedEstimatorDetails);
        return;
      }
    }

    if (!country || monthlyEnergyNeed < 1 || maxPower < 1) {
      return;
    }

    const fetchPreferences = async () => {
      try {
        let brandPriority: ESolarSimBrands[] = [];
        if (configurations) {
          if (Array.isArray(configurations.BRAND_PRIORITY_LIST)) {
            brandPriority = configurations.BRAND_PRIORITY_LIST.map(
              (bp) => bp.brand,
            );
          }
        }

        const brandPriorityPreference = configurations
          ? Boolean(configurations.BRAND_PRIORITY)
          : false;
        const optimizeForCost = configurations
          ? Boolean(configurations.OPTIMIZE_FOR_COST)
          : false;
        const preferHighEfficiencyPanels = configurations
          ? Boolean(configurations.PREFER_HIGH_EFFICIENCY_PANELS)
          : false;
        const recommendExpandableSystems = configurations
          ? Boolean(configurations.RECOMMEND_EXPANDABLE_SYSTEMS)
          : false;

        if (brandPriority.length === 0) {
          return;
        }

        await triggerCreateSolarEstimation({
          estimationId: auth.token ? estimationId : "",
          estimatorToken: auth.token ? "" : estimationToken,
          title: title ? title : null,
          estimationType: solarSolutionType,
          applicationType:
            solarSolutionType === ESolarSolutionType.BOTH
              ? (applicationType.value as EApplicationType)
              : null,
          utilityOptionType:
            solarSolutionType === ESolarSolutionType.BOTH
              ? (utilityOption.value as EUtilityOptionType)
              : null,
          solarEstimationType: EEstimationType.DIRECT,
          systemType: solarSystemType.value as ESolarSystemType,
          inverterPhaseType: inverterPhaseType.value as EInverterPhaseType,
          monthlyRequiredEnergy: monthlyEnergyNeed,
          maxRequiredPower: maxPower,
          numberOfUsers:
            solarSolutionType === ESolarSolutionType.BOTH
              ? numberOfUsers
              : null,
          demandPerUser:
            solarSolutionType === ESolarSolutionType.BOTH
              ? demandPerUser
              : null,
          nightLoad: nightLoad,
          systemVoltage: systemVoltage ? Number(systemVoltage.value) : null,
          autonomyDays: autonomyDays,
          sunlightHours: sunlightHours,
          estimationSpaceClusterId: null,
          estimationBasicScheduleId: null,
          isDraft: false,
          brandPriority,
          brandPriorityPreference,
          optimizeForCost,
          preferHighEfficiencyPanels,
          recommendExpandableSystems,
          countryCode: country,
          hasChanged: hasChanged || null,
          controllerRequired: controllerRequired,
        })
          .unwrap()
          .then(() => {
            localStorage.removeItem("globalConfigurations");
          });

        await triggerLazyViewSolarEstimation(
          auth.token
            ? {
                estimationId: estimationId,
              }
            : {
                estimatorToken: estimationToken,
              },
        )
          .unwrap()
          .then((viewRes) => {
            setEstimationId(viewRes.estimationView.estimationId);
            setAssembleSystemData({ ...viewRes.estimationView.data });
            setSelectedSystemOutput(
              viewRes.estimationView.data.outputParams || {
                totalWattHours: 0,
                totalCost: 0,
                systemVoltage: 0,
                totalAmpHours: 0,
                nightLoadAmpHours: 0,
                batteryAmpHours: 0,
                maxPowerInWatt: 0,
                grandTotal: 0,
                totalAccessoriesCost: 0,
                laborCost: 0,
                totalLiters: 0,
              },
            );
            setDischargeRate(
              viewRes.estimationView.data.inputParams.dischargeRatePercentage ??
                0,
            );
          });
      } catch {
        setEstimationId(0);
        setAssembleSystemData(undefined);
      }
    };

    fetchPreferences();
  }, [
    pathname,
    estimatorDetails.estimationId,
    estimatorDetails.estimationToken,
    title,
    monthlyEnergyNeed,
    maxPower,
    nightLoad,
    sunlightHours,
    isResetRecommendations,
    country,
    configurations,
    solarSystemType,
    inverterPhaseType,
    solarSolutionType,
    applicationType,
    utilityOption,
    systemVoltage,
    autonomyDays,
    numberOfUsers,
    demandPerUser,
    controllerRequired,
  ]);

  useEffect(() => {
    if (!estimatorDetails.estimationToken || !estimatorDetails.estimationId) {
      const unparsedEstimatorDetails = localStorage.getItem(
        ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
      );

      if (unparsedEstimatorDetails) {
        const parsedEstimatorDetails: IEstimatorDetails = JSON.parse(
          unparsedEstimatorDetails,
        );
        setEstimatorDetails(parsedEstimatorDetails);
        return;
      }
    }

    if (
      pathname.includes("system-summary") &&
      estimatorDetails.estimationToken
    ) {
      triggerLazyViewSolarEstimation(
        auth.token
          ? {
              estimationId: estimatorDetails.estimationId,
            }
          : {
              estimatorToken: estimatorDetails.estimationToken,
            },
      )
        .unwrap()
        .then((res) => {
          setComment(res.estimationView.adminComment || "");
          setSelectedSystemOutput(
            res.estimationView.data.outputParams || {
              totalWattHours: 0,
              totalCost: 0,
              systemVoltage: 0,
              totalAmpHours: 0,
              nightLoadAmpHours: 0,
              batteryAmpHours: 0,
              maxPowerInWatt: 0,
              grandTotal: 0,
              totalAccessoriesCost: 0,
              laborCost: 0,
              totalLiters: 0,
            },
          );
          setUserSelectedProducts(
            res.estimationView.data.userSelectedProducts || [],
          );
          setAssembleSystemData({ ...res.estimationView.data });
          setCurrency(res.estimationView.currency);
        })
        .catch(() => {
          setComment("");
          setSelectedSystemOutput({
            totalWattHours: 0,
            totalCost: 0,
            systemVoltage: 0,
            totalAmpHours: 0,
            nightLoadAmpHours: 0,
            batteryAmpHours: 0,
            maxPowerInWatt: 0,
            grandTotal: 0,
            totalAccessoriesCost: 0,
            laborCost: 0,
            totalLiters: 0,
          });
          setUserSelectedProducts([]);
          setAssembleSystemData(undefined);
          setCurrency("");
        });
    }
  }, [
    pathname,
    estimatorDetails.estimationToken,
    estimatorDetails.estimationId,
  ]);

  useEffect(() => {
    if (!pathname.includes("system-summary")) {
      setUserSelectedProducts([]);
      setAssembleSystemData(undefined);
      setCurrency("");
    }

    if (!pathname.includes("assemble-system")) {
      setAssembleSystemData(undefined);
    }
  }, [pathname]);

  useEffect(() => {
    if (locationState && locationState?.estimationId) {
      triggerLazyViewSolarEstimation(
        auth.token
          ? {
              estimationId: locationState.estimationId,
            }
          : {
              estimatorToken: locationState?.estimationToken,
            },
      )
        .unwrap()
        .then((res: ViewSolarEstimationResponseDTO) => {
          setEstimationId(res.estimationView.estimationId);

          triggerUpdateEstimation({
            estimationId: auth.token ? locationState?.estimationId : "",
            estimatorToken: auth.token ? "" : locationState?.estimationToken,
            title: res.estimationView.title,
            estimationType: locationState.solarSolutionType,
            applicationType: res.estimationView.applicationType,
            utilityOptionType: res.estimationView.utilityOptionType,
            solarEstimationType: EEstimationType.DIRECT,
            systemType: locationState.systemType,
            inverterPhaseType: locationState.inverterPhaseType,
            monthlyRequiredEnergy: locationState.monthlyEnergyNeed,
            maxRequiredPower: locationState.maxPowerNeed * 1000,
            numberOfUsers: res.estimationView.data.inputParams.numberOfUsers,
            demandPerUser: res.estimationView.data.inputParams.demandPerUser,
            nightLoad: locationState.nightLoad,
            sunlightHours: locationState.sunlightHours,
            systemVoltage: locationState.systemVoltage,
            autonomyDays: locationState.autonomyDays,
            estimationSpaceClusterId: null,
            estimationBasicScheduleId: null,
            isDraft: false,
            countryCode: res.estimationView.countryCode,
            installationCostRequested:
              res.estimationView.installationCostRequested,
          })
            .unwrap()
            .then((res: ISolarEstimationIdAndToken) => {
              setEstimatorDetails({
                estimationId: res.id,
                estimationToken: res.token,
              });
              setIsUpdateCurrentState((ps) => !ps);
            });
        });

      return;
    }

    if (locationState) {
      triggerInitiateSolarEstimation({
        estimationType: locationState.solarSolutionType,
        applicationType:
          locationState.solarSolutionType === ESolarSolutionType.BOTH
            ? locationState.applicationType
            : null,
        utilityOptionType:
          locationState.solarSolutionType === ESolarSolutionType.BOTH
            ? locationState.utilityOption
            : null,
        solarEstimationType: EEstimationType.DIRECT,
        systemType: locationState.systemType,
        inverterPhaseType: locationState.inverterPhaseType,
        monthlyRequiredEnergy: locationState.monthlyEnergyNeed,
        maxRequiredPower: locationState.maxPowerNeed * 1000,
        numberOfUsers:
          locationState.solarSolutionType === ESolarSolutionType.BOTH
            ? locationState.numberOfUsers
            : null,
        demandPerUser:
          locationState.solarSolutionType === ESolarSolutionType.BOTH
            ? locationState.demandPerUser
            : null,
        nightLoad: locationState.nightLoad,
        systemVoltage: locationState.systemVoltage,
        autonomyDays: locationState.autonomyDays,
        sunlightHours: locationState.sunlightHours,
        countryCode: country,
      })
        .unwrap()
        .then((res) => {
          const selectedSolarTypeOption = solarSystemTypesOption.find(
            (s) => s.value === locationState.systemType,
          );

          const selectedSystemVoltageOption = systemVoltageOptions.find(
            (s) => Number(s.value) === locationState.systemVoltage,
          );

          const selectedInverterPhaseTypeOption = inverterPhaseTypeOptions.find(
            (i) => i.value === locationState.inverterPhaseType,
          );

          const selectedUtilityOption = utilityOptions.find(
            (u) => u.value === locationState.utilityOption,
          );

          const selectedApplicationType = applicationTypeOptions.find(
            (a) => a.value === locationState.applicationType,
          );

          setMonthlyEnergyNeed(locationState.monthlyEnergyNeed);
          setMaxPower(locationState.maxPowerNeed * 1000);
          setAutonomyDays(locationState.autonomyDays);
          setNightLoad(locationState.nightLoad);
          setSunlightHours(locationState.sunlightHours);
          setEstimatorDetails({
            estimationId: res.id,
            estimationToken: res.token,
          });
          setNumberOfUsers(locationState.numberOfUsers);
          setDemandPerUser(locationState.demandPerUser);
          setSolarSolutionType(locationState.solarSolutionType);

          if (selectedSolarTypeOption) {
            setSolarSystemType({
              value: selectedSolarTypeOption.value,
              label: selectedSolarTypeOption.label,
            });
          }

          if (selectedSystemVoltageOption) {
            setSystemVoltage({
              value: selectedSystemVoltageOption.value,
              label: selectedSystemVoltageOption.label,
            });
          }

          if (selectedInverterPhaseTypeOption) {
            setInverterPhaseType({
              value: selectedInverterPhaseTypeOption.value,
              label: selectedInverterPhaseTypeOption.label,
            });
          }

          if (selectedApplicationType) {
            setApplicationType({
              value: selectedApplicationType.value,
              label: selectedApplicationType.label,
            });
          }

          if (selectedUtilityOption) {
            setUtilityOption({
              value: selectedUtilityOption.value,
              label: selectedUtilityOption.label,
            });
          }

          localStorage.setItem(
            ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
            JSON.stringify({
              estimationId: res.id,
              estimationToken: res.token,
            }),
          );

          triggerLazyViewSolarEstimation(
            auth.token
              ? {
                  estimationId: res.id,
                }
              : {
                  estimatorToken: res.token,
                },
          )
            .unwrap()
            .then((viewRes) => {
              setEstimationId(viewRes.estimationView.estimationId);
              navigate(location.pathname, { replace: true, state: undefined });
            })
            .catch(() => {
              setEstimationId(0);
            });
        });
    }
  }, [
    triggerInitiateSolarEstimation,
    triggerLazyViewSolarEstimation,
    locationState,
    auth.token,
  ]);

  const onStepClick = (stepNumber: number) => {
    if (selectedStep === stepNumber) return;

    if (auth.token) {
      if (stepNumber === 1) {
        navigate(AppRoute.DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM);
        return;
      }

      if (stepNumber === 2) {
        navigate(AppRoute.DESIGN_SYSTEM_DIRECT_SYSTEM_SUMMARY);
        return;
      }
      return;
    }

    if (stepNumber === 1) {
      navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM);
      return;
    }

    if (stepNumber === 2) {
      navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_DIRECT_SYSTEM_SUMMARY);
    }
  };

  const onSave = async (
    title: string,
    estimationType: ESolarSolutionType,
    applicationType: EApplicationType,
    utilityOptionType: EUtilityOptionType,
    systemType: ESolarSystemType,
    inverterPhaseType: EInverterPhaseType,
    monthlyEnergyNeed: number,
    maxPower: number,
    numberOfUsers: number,
    nightLoad: number,
    sunlightHours: number,
    demandPerUser: number | null,
    systemVoltage: number | null,
    autonomyDays: number | null,
    stepNumber?: number,
  ) => {
    if (selectedStep === stepNumber) return;

    if (
      !title.trim() &&
      !isRemoveWaterHeaterSystem &&
      !isAddWaterHeaterSystem
    ) {
      showErrorMessage("Please provide a title before continuing");
      return;
    }

    const directSave: IDirectSave = {
      title,
      estimationType,
      applicationType,
      utilityOptionType,
      systemType,
      inverterPhaseType,
      monthlyEnergyNeed,
      maxPower,
      numberOfUsers,
      nightLoad,
      sunlightHours,
      demandPerUser,
      systemVoltage,
      autonomyDays,
      isCalledSave: false,
      includeOtherFees: includeOtherFees,
    };

    if (!auth.token && !isRemoveWaterHeaterSystem && !isAddWaterHeaterSystem) {
      if (pathname.includes("assemble-system")) {
        localStorage.setItem(
          "redirect",
          AppRoute.DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM,
        );
      } else if (pathname.includes("system-summary")) {
        localStorage.setItem(
          "redirect",
          AppRoute.DESIGN_SYSTEM_DIRECT_SYSTEM_SUMMARY,
        );
      }

      localStorage.setItem("directSave", JSON.stringify(directSave));
      setIsShowSaveDataModal(true);
      return;
    }

    try {
      directSave.isCalledSave = true;
      localStorage.setItem("directSave", JSON.stringify(directSave));
      if (!estimatorDetails.estimationId || !estimatorDetails.estimationToken) {
        return;
      }

      await triggerUpdateEstimation({
        estimationId: auth.token ? estimatorDetails.estimationId : "",
        estimatorToken: auth.token ? "" : estimatorDetails.estimationToken,
        title: title ? title : null,
        estimationType,
        applicationType:
          estimationType === ESolarSolutionType.BOTH ? applicationType : null,
        utilityOptionType:
          estimationType === ESolarSolutionType.BOTH ? utilityOptionType : null,
        solarEstimationType: EEstimationType.DIRECT,
        systemType,
        inverterPhaseType: inverterPhaseType,
        monthlyRequiredEnergy: monthlyEnergyNeed,
        maxRequiredPower: maxPower,
        numberOfUsers:
          estimationType === ESolarSolutionType.BOTH ? numberOfUsers : null,
        demandPerUser:
          estimationType === ESolarSolutionType.BOTH ? demandPerUser : null,
        nightLoad,
        sunlightHours,
        systemVoltage,
        autonomyDays,
        estimationSpaceClusterId: null,
        estimationBasicScheduleId: null,
        isDraft: false,
        countryCode: country,
        installationCostRequested: includeOtherFees,
      })
        .unwrap()
        .then(() => {
          if (isRemoveWaterHeaterSystem) {
            showSuccessMessage("Water Heater System Deleted Successfully");
          }

          if (isAddWaterHeaterSystem) {
            showSuccessMessage("Water Heater System Added Successfully");
          }

          if (!isRemoveWaterHeaterSystem && !isAddWaterHeaterSystem) {
            showSuccessMessage("Estimation Saved Successfully");
          }

          if (stepNumber) {
            onStepClick(stepNumber);
          }

          setIsUpdateCurrentState((ps) => !ps);
          setIsRemoveWaterHeaterSystem(false);
          setIsAddWaterHeaterSystem(false);
          setIsShowRemoveWaterHeaterSystemModal(false);
          setIsShowRemoveSolarSystemModal(false);
          setIsShowWaterHeaterSystemModal(false);
        });
    } catch {
      showErrorMessage("Failed to save");
    }
  };

  const onEditParameter = (type: EEnergyNeedEditParameter, value: string) => {
    let newValue;
    if (type === EEnergyNeedEditParameter.TITLE) {
      newValue = value;
    } else {
      newValue = value.trim() === "" ? 0 : value;
    }

    switch (type) {
      case EEnergyNeedEditParameter.TITLE:
        setTitle(newValue as string);
        break;
      case EEnergyNeedEditParameter.MONTHLY_ENERGY_NEED: {
        const previousValue = monthlyEnergyNeed;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setMonthlyEnergyNeed(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }

        break;
      }
      case EEnergyNeedEditParameter.MAX_POWER: {
        const previousValue = maxPower;
        const nowValue = Number.isNaN(Number(newValue))
          ? 0
          : Number(newValue) * 1000;

        if (previousValue !== nowValue) {
          setMaxPower(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue) * 1000,
          );
          setIsReset(true);
        }

        break;
      }
      case EEnergyNeedEditParameter.NIGHT_LOAD: {
        const previousValue = nightLoad;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setNightLoad(Number.isNaN(Number(newValue)) ? 0 : Number(newValue));
          setIsReset(true);
        }

        break;
      }
      case EEnergyNeedEditParameter.SUNLIGHT_HOURS: {
        const previousValue = sunlightHours;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setSunlightHours(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }

        break;
      }
      case EEnergyNeedEditParameter.NO_OF_USERS: {
        const previousValue = numberOfUsers;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setNumberOfUsers(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }

        break;
      }
      case EEnergyNeedEditParameter.DEMAND_PER_USER: {
        const previousValue = demandPerUser;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setDemandPerUser(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }

        break;
      }
      case EEnergyNeedEditParameter.AUTONOMY_DAYS: {
        const previousValue = autonomyDays;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setAutonomyDays(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }

        break;
      }
      default:
        break;
    }

    setShowEditParameterModal(false);
  };

  const onParameterEdit = (type: EEnergyNeedEditParameter) => {
    setEditParameterType(type);
    let editValue: string | number;
    if (type === EEnergyNeedEditParameter.TITLE) {
      editValue = title;
    } else if (type === EEnergyNeedEditParameter.MONTHLY_ENERGY_NEED) {
      editValue = monthlyEnergyNeed;
    } else if (type === EEnergyNeedEditParameter.MAX_POWER) {
      editValue = (maxPower / 1000).toFixed(2);
    } else if (type === EEnergyNeedEditParameter.NIGHT_LOAD) {
      editValue = nightLoad;
    } else if (type === EEnergyNeedEditParameter.SUNLIGHT_HOURS) {
      editValue = sunlightHours;
    } else if (type === EEnergyNeedEditParameter.NO_OF_USERS) {
      editValue = numberOfUsers;
    } else if (type === EEnergyNeedEditParameter.AUTONOMY_DAYS) {
      editValue = autonomyDays ?? 0;
    } else {
      editValue = demandPerUser ?? 0;
    }

    setEditParameterValue(editValue);
    let estimatedVal: number | undefined;
    if (type === EEnergyNeedEditParameter.MONTHLY_ENERGY_NEED) {
      estimatedVal = monthlyEnergyNeed;
    } else if (type === EEnergyNeedEditParameter.MAX_POWER) {
      estimatedVal = maxPower;
    } else {
      estimatedVal = undefined;
    }
    setEstimatedValue(estimatedVal);
    setShowEditParameterModal(true);
  };

  return (
    <>
      <StepsHeader
        steps={steps}
        selectedStep={selectedStep}
        onStepClick={(stepNumber: number) => {
          if (selectedStep === stepNumber) return;
          if (!title.trim()) {
            showErrorMessage("Please provide a title before continuing");
            return;
          }

          if (!auth.token) {
            onStepClick(stepNumber);
            return;
          }

          onSave(
            title,
            solarSolutionType,
            applicationType.value as EApplicationType,
            utilityOption.value as EUtilityOptionType,
            solarSystemType.value as ESolarSystemType,
            inverterPhaseType.value as EInverterPhaseType,
            monthlyEnergyNeed,
            maxPower,
            numberOfUsers,
            nightLoad,
            sunlightHours,
            demandPerUser,
            systemVoltage ? Number(systemVoltage.value) : null,
            autonomyDays,
            stepNumber,
          );
        }}
        onSave={() => {
          onSave(
            title,
            solarSolutionType,
            applicationType.value as EApplicationType,
            utilityOption.value as EUtilityOptionType,
            solarSystemType.value as ESolarSystemType,
            inverterPhaseType.value as EInverterPhaseType,
            monthlyEnergyNeed,
            maxPower,
            numberOfUsers,
            nightLoad,
            sunlightHours,
            demandPerUser,
            systemVoltage ? Number(systemVoltage.value) : null,
            autonomyDays,
          );
        }}
        isApproved={false}
      />
      <div className={`py-3 ${styles.mainContent}`}>
        <div
          className={`estimation-container rounded-3 ${
            auth.token ? " mx-0" : "mx-4"
          }`}
        >
          <Row
            className={`align-items-center justify-content-between py-2 px-4 ${
              solarSolutionType === ESolarSolutionType.BOTH ? `mb-2` : ``
            }`}
          >
            <Col className="col-auto">
              <Row>
                <Col className="text-color-3 font-size-14">#{estimationId}</Col>
              </Row>
              <Row className="align-items-center">
                <Col className="col-auto title-2 font-weight-600 font-size-18 pe-0">
                  {title === "" ? "Title Goes Here" : title}
                </Col>
                <Col className="col-auto ps-1">
                  <MaterialIcon
                    icon="border_color"
                    color="var(--color-edit-icon-2)"
                    size={14}
                    className="cursor-pointer"
                    onClick={() => {
                      onParameterEdit(EEnergyNeedEditParameter.TITLE);
                    }}
                  />
                </Col>
              </Row>
            </Col>
            <Col
              className={
                solarSolutionType === ESolarSolutionType.BOTH
                  ? `col-12 mt-2`
                  : `col-12 col-xxl-auto mt-3 mt-xxl-0`
              }
            >
              <Row className="align-items-stretch gx-2">
                <Col
                  className={
                    solarSolutionType === ESolarSolutionType.BOTH
                      ? `col-12 col-xxl-7`
                      : ``
                  }
                >
                  <SelectedSolarSystemCard
                    monthlyEnergyNeed={monthlyEnergyNeed}
                    maxPower={maxPower}
                    dischargeRate={dischargeRate}
                    nightLoad={nightLoad}
                    sunlightHours={sunlightHours}
                    autonomyDays={autonomyDays}
                    monthlyEnergyNeedFromUser={0}
                    maxPowerFromUser={0}
                    onParameterEdit={onParameterEdit}
                    totalWattHours={selectedSystemOutput.totalWattHours}
                    maxPowerInWatt={selectedSystemOutput.maxPowerInWatt}
                    solarSystemType={solarSystemType}
                    onChangeSolarSystemType={(option: Option) => {
                      setSolarSystemType(option);
                      setAutonomyDays(null);
                      setIsReset(true);
                    }}
                    systemVoltage={systemVoltage}
                    onSystemVoltageChange={(option: Option) => {
                      setSystemVoltage(option);
                      setIsReset(true);
                    }}
                    inverterPhaseType={inverterPhaseType}
                    onChangeInverterPhaseType={(option: Option) => {
                      setInverterPhaseType(option);
                      setIsReset(true);
                    }}
                    isHideDeleteIcon={
                      solarSolutionType !== ESolarSolutionType.BOTH ||
                      selectedStep === 2
                    }
                    onDeleteSolarSystem={() =>
                      setIsShowRemoveSolarSystemModal(true)
                    }
                    isHideEditIcon={selectedStep === 2}
                  />
                </Col>
                <Col
                  className={
                    solarSolutionType === ESolarSolutionType.BOTH
                      ? `col-12 col-xxl-5 mt-3 mt-xxl-0`
                      : `col-12 col-xxl-auto mt-3 mt-xxl-0`
                  }
                >
                  {solarSolutionType === ESolarSolutionType.BOTH ? (
                    <SelectedWaterHeaterSystemCard
                      onParameterEdit={onParameterEdit}
                      waterDemand={waterDemand}
                      totalLiters={selectedSystemOutput.totalLiters || 0}
                      numberOfUsers={numberOfUsers}
                      demandPerUser={demandPerUser}
                      applicationType={applicationType}
                      utilityOption={utilityOption}
                      onApplicationTypeChange={(option: Option) => {
                        setApplicationType(option);
                        setIsReset(true);
                      }}
                      onUtilityOptionChange={(option: Option) => {
                        setUtilityOption(option);
                        setIsReset(true);
                      }}
                      onDeleteWaterHeaterSystem={() => {
                        setIsShowRemoveWaterHeaterSystemModal(true);
                        setIsRemoveWaterHeaterSystem(true);
                      }}
                      isHideDeleteIcon={selectedStep === 2}
                      isHideEditIcon={selectedStep === 2}
                    />
                  ) : (
                    <>
                      {selectedStep === 2 ? (
                        <></>
                      ) : (
                        <IncludeSystemCard
                          icon="water_drop"
                          text="Include Solar Water Heating System as Well?"
                          onClick={() => {
                            setIsShowWaterHeaterSystemModal(true);
                            setIsAddWaterHeaterSystem(true);
                          }}
                        />
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
          {selectedStep === 1 ? (
            <AssembleSystem
              assembleSystemData={assembleSystemData}
              estimatorDetails={estimatorDetails}
              selectedSystemOutput={selectedSystemOutput}
              setSelectedSystemOutput={setSelectedSystemOutput}
              setIsResetRecommendations={setIsResetRecommendations}
              configurations={configurations}
              sendConfigurations={(configurations: Configurations) => {
                setConfigurations({ ...configurations });
              }}
              isReset={isReset}
              setIsReset={setIsReset}
              setHasChanged={setHasChanged}
              estimationNo={estimationId}
              setDischargeRate={setDischargeRate}
              estimationType={solarSolutionType}
              setWaterDemand={setWaterDemand}
              controllerRequired={controllerRequired}
              setControllerRequired={setControllerRequired}
              systemType={solarSystemType.value as ESolarSystemType}
              isDisabledControllerSwitch={
                isLoadingCreateSolarEstimation ||
                isLoadingViewEstimation ||
                isLoadingUpdateEstimation
              }
            />
          ) : (
            <ApprovedSystem
              userSelectedProducts={userSelectedProducts}
              assembleSystemData={assembleSystemData}
              onSave={() => {
                onSave(
                  title,
                  solarSolutionType,
                  applicationType.value as EApplicationType,
                  utilityOption.value as EUtilityOptionType,
                  solarSystemType.value as ESolarSystemType,
                  inverterPhaseType.value as EInverterPhaseType,
                  monthlyEnergyNeed,
                  maxPower,
                  numberOfUsers,
                  nightLoad,
                  sunlightHours,
                  demandPerUser,
                  systemVoltage ? Number(systemVoltage.value) : null,
                  autonomyDays,
                );
              }}
              currency={currency}
              estimatorDetails={estimatorDetails}
              includeOtherFees={includeOtherFees}
              setIncludeOtherFees={setIncludeOtherFees}
              comment={comment}
              title={title}
              country={country}
              monthlyRequiredEnergy={monthlyEnergyNeed}
              maxRequiredPower={maxPower}
              systemType={solarSystemType.value as ESolarSystemType}
              inverterPhaseType={inverterPhaseType.value as EInverterPhaseType}
              nightLoad={nightLoad}
              sunlightHours={sunlightHours}
              systemVoltage={systemVoltage ? Number(systemVoltage.value) : null}
              autonomyDays={autonomyDays}
              estimationSpaceClusterId={null}
              estimationNo={estimationId}
              estimationType={solarSolutionType}
              applicationType={applicationType.value as EApplicationType}
              utilityOptionType={utilityOption.value as EUtilityOptionType}
              numberOfUsers={numberOfUsers}
              demandPerUser={demandPerUser}
            />
          )}
        </div>
      </div>
      <EditParameterModal
        show={showEditParameterModal}
        onCancel={() => {
          setShowEditParameterModal(false);
        }}
        onClose={() => {
          setShowEditParameterModal(false);
        }}
        type={editParameterType}
        currentValue={editParameterValue}
        onEditParameter={onEditParameter}
        estimated={estimatedValue}
      />
      <SpinnerModal
        show={
          isLoadingInitiateSolarEstimation ||
          isLoadingCreateSolarEstimation ||
          isLoadingViewEstimation ||
          isLoadingUpdateEstimation
        }
      />
      <SaveDataModal
        show={isShowSaveDataModal}
        onClose={() => {
          const directSave = localStorage.getItem("directSave");

          if (directSave) {
            const parsedDirectSave: IDirectSave = JSON.parse(directSave);
            parsedDirectSave.isCalledSave = true;
            localStorage.setItem(
              "directSave",
              JSON.stringify(parsedDirectSave),
            );
          }

          localStorage.removeItem("redirect");

          setIsShowSaveDataModal(false);
        }}
        onConfirm={() => {
          navigate(AppRoute.LOGIN);
          setIsShowSaveDataModal(false);
        }}
      />
      <RemoveSolarSystemModal
        show={isShowRemoveSolarSystemModal}
        onClose={() => setIsShowRemoveSolarSystemModal(false)}
        onCancel={() => setIsShowRemoveSolarSystemModal(false)}
        onConfirm={() => {
          triggerUpdateEstimation({
            estimationId: auth.token ? estimatorDetails.estimationId : "",
            estimatorToken: auth.token ? "" : estimatorDetails.estimationToken,
            title: title ? title : null,
            estimationType: ESolarSolutionType.WATER_HEATING_SYSTEM,
            applicationType: applicationType.value as EApplicationType,
            utilityOptionType: utilityOption.value as EUtilityOptionType,
            solarEstimationType: null,
            systemType: null,
            inverterPhaseType: null,
            monthlyRequiredEnergy: null,
            maxRequiredPower: null,
            numberOfUsers: numberOfUsers,
            demandPerUser: demandPerUser,
            nightLoad: null,
            systemVoltage: null,
            autonomyDays: null,
            sunlightHours: null,
            estimationSpaceClusterId: null,
            estimationBasicScheduleId: null,
            isDraft: false,
            countryCode: country,
            installationCostRequested: includeOtherFees,
          })
            .unwrap()
            .then(() => {
              showSuccessMessage("Solar Energy System Deleted Successfully");
              setIsShowRemoveSolarSystemModal(false);

              if (auth.token) {
                navigate(AppRoute.DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM, {
                  state: {
                    estimationId: estimatorDetails.estimationId,
                    estimationToken: estimatorDetails.estimationToken,
                  },
                });
                return;
              }

              navigate(
                AppRoute.PUBLIC_DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM,
                {
                  state: {
                    estimationId: estimatorDetails.estimationId,
                    estimationToken: estimatorDetails.estimationToken,
                  },
                },
              );
            });
        }}
        isLoading={isLoadingUpdateEstimation}
      />
      <RemoveWaterHeaterSystemModal
        show={isShowRemoveWaterHeaterSystemModal}
        onClose={() => {
          setIsShowRemoveWaterHeaterSystemModal(false);
          setIsRemoveWaterHeaterSystem(false);
        }}
        onCancel={() => {
          setIsShowRemoveWaterHeaterSystemModal(false);
          setIsRemoveWaterHeaterSystem(false);
        }}
        onConfirm={() => {
          onSave(
            title,
            ESolarSolutionType.SOLAR_ENERGY_SYSTEM,
            applicationType.value as EApplicationType,
            utilityOption.value as EUtilityOptionType,
            solarSystemType.value as ESolarSystemType,
            inverterPhaseType.value as EInverterPhaseType,
            monthlyEnergyNeed,
            maxPower,
            numberOfUsers,
            nightLoad,
            sunlightHours,
            demandPerUser,
            systemVoltage ? Number(systemVoltage.value) : null,
            autonomyDays,
          );
        }}
        isLoading={isLoadingUpdateEstimation}
      />
      <WaterHeaterSystemModal
        show={isShowWaterHeaterSystemModal}
        onClose={() => {
          setIsShowWaterHeaterSystemModal(false);
          setIsAddWaterHeaterSystem(false);
        }}
        onCancel={() => {
          setIsShowWaterHeaterSystemModal(false);
          setIsAddWaterHeaterSystem(false);
        }}
        onSave={(
          applicationType,
          utilityOption,
          numberOfUsers,
          demandPerUser,
        ) => {
          onSave(
            title,
            ESolarSolutionType.BOTH,
            applicationType,
            utilityOption,
            solarSystemType.value as ESolarSystemType,
            inverterPhaseType.value as EInverterPhaseType,
            monthlyEnergyNeed,
            maxPower,
            numberOfUsers,
            nightLoad,
            sunlightHours,
            demandPerUser,
            systemVoltage ? Number(systemVoltage.value) : null,
            autonomyDays,
          );
        }}
        isLoading={isLoadingUpdateEstimation}
      />
    </>
  );
};

export default BuildSystemDirect;
